import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/articles';

const categoryHeroImages: Record<string, string> = {
  'wanghong-tafang': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20a%20spotlight%20shattering%20on%20a%20stage%2C%20warm%20coral%20red%20and%20dark%20amber%20gradients%2C%20dramatic%20revelation%20theme%2C%20minimalist%20art%20style%2C%20clean%20composition%2C%20editorial%20quality&width=1920&height=1080&seq=cat-hero-wanghong&orientation=landscape',
  'heiliao-baoguang': 'https://readdy.ai/api/search-image?query=Mysterious%20abstract%20digital%20art%20with%20magnifying%20glass%20revealing%20hidden%20truth%2C%20warm%20amber%20and%20deep%20coral%20gradients%2C%20investigation%20and%20exposure%20theme%2C%20minimalist%20modern%20style%2C%20clean%20composition%2C%20editorial%20quality&width=1920&height=1080&seq=cat-hero-heiliao&orientation=landscape',
  'chigua-shipin': 'https://readdy.ai/api/search-image?query=Vibrant%20abstract%20composition%20of%20floating%20social%20media%20video%20elements%2C%20warm%20coral%20pink%20and%20golden%20amber%20gradients%2C%20energetic%20and%20playful%20digital%20art%2C%20minimalist%20style%2C%20clean%20composition%2C%20editorial%20quality&width=1920&height=1080&seq=cat-hero-chigua&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到" />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
          <div className="text-center px-4">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">分类未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">请检查链接是否正确</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);
  const topArticle = categoryArticles.find((a) => a.isTop);
  const regularArticles = categoryArticles.filter((a) => !a.isTop);

  return (
    <>
      <SEOHead
        title={`${category.name} - 最新${category.name}资讯`}
        description={`${category.description}。51网址导航${category.name}频道，24小时实时更新最新${category.name}资讯。`}
        keywords={`${category.name},51网址导航,网红八卦,吃瓜,实时更新`}
        pageType="website"
        ogImage={category.imageUrl}
      />

      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={categoryHeroImages[slug] || category.imageUrl}
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center pt-16">
            <nav className="flex items-center justify-center gap-2 text-white/70 text-xs mb-4" aria-label="breadcrumb">
              <Link to="/" className="hover:text-white transition-colors duration-200 whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-white">{category.name}</span>
            </nav>
            <h1 className="text-3xl md:text-5xl font-heading text-white mb-4">
              {category.name}
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-6">
              {category.description}
            </p>
            <div className="flex items-center justify-center gap-4 text-white/60 text-xs">
              <span><i className="ri-file-text-line mr-1"></i>共 {category.count} 篇</span>
              <span><i className="ri-time-line mr-1"></i>24H 实时更新</span>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <span className="text-foreground-900 font-medium">{category.name}</span>
          </nav>
        </div>

        {/* Featured Article */}
        {topArticle && (
          <section className="pb-10 md:pb-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg font-heading text-foreground-900 mb-4">编辑精选</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Link
                  to={`/article/${topArticle.id}`}
                  className="group relative rounded-2xl overflow-hidden aspect-[4/5] lg:aspect-auto"
                >
                  <img
                    src={topArticle.imageUrl}
                    alt={topArticle.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                    <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-3">
                      本周精选
                    </span>
                    <h3 className="text-white text-lg md:text-xl font-heading mb-2 line-clamp-2">
                      {topArticle.title}
                    </h3>
                    <p className="text-white/70 text-sm line-clamp-2">
                      {topArticle.excerpt}
                    </p>
                    <div className="flex items-center gap-3 mt-3 text-white/60 text-xs">
                      <span>{topArticle.views} 阅读</span>
                      <span>{topArticle.publishDate}</span>
                    </div>
                  </div>
                </Link>

                <div className="flex flex-col gap-6">
                  {regularArticles.slice(0, 2).map((article, idx) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group relative rounded-2xl overflow-hidden flex-1 min-h-[180px]"
                    >
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className={`absolute inset-0 ${
                        idx === 0 ? 'bg-secondary-500/30' : 'bg-accent-500/20'
                      }`}></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                        <h3 className="text-white text-base md:text-lg font-heading line-clamp-2 mb-1">
                          {article.title}
                        </h3>
                        <div className="flex items-center gap-3 text-white/60 text-xs">
                          <span>{article.views} 阅读</span>
                          <span>{article.duration}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* All Articles Grid */}
        <section className="py-10 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-lg font-heading text-foreground-900 mb-6">
              全部{category.name}资讯
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {categoryArticles.map((article, index) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-2xl p-5 flex flex-col transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden mb-4">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 ${
                      index % 3 === 0 ? 'bg-primary-500/10' : index % 3 === 1 ? 'bg-accent-500/10' : 'bg-secondary-500/10'
                    }`}></div>
                  </div>
                  <h3 className="text-base font-heading text-foreground-900 mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-500 mt-auto">
                    <span>{article.views} 阅读</span>
                    <span>{article.publishDate}</span>
                  </div>
                  <div className={`w-full py-2 rounded-full text-center text-sm font-medium mt-4 transition-colors duration-200 ${
                    index === 0
                      ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                      : 'bg-background-50 text-foreground-700 border border-foreground-300 hover:bg-background-100'
                  }`}>
                    查看详情
                  </div>
                  <p className="text-xs text-foreground-400 text-center mt-2">
                    {article.duration} · 来源：51网址导航
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}